// Setup › Workforce › Who may direct my agents (`/c/{slug}/setup/agent-direction`; COS-MODES,
// docs/design/2026-10-30-chief-of-staff-model.md §3): a person's own card. The level for their
// department (Hands-off, Tell me, Ask me first), the two action types Ask me first holds (new work,
// priorities), its timeout in working hours and what happens after it; urgent and configuration
// are fixed rows. When the company locked the level, the card reads the company's and is not
// editable. Every member opens it; one `GET`, one `PUT` (`agentDirection.*`). With the `agents`
// add-on off the page is the add-on banner alone (no call).

import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SaveIndicator,
  SettingsSection,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LockIcon, ShieldCheckIcon, WaypointsIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { AgentsOffBanner } from "../agents/agents-ux";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { useFeature } from "../use-feature";
import { problemText } from "./problem-text";
import { SetupPage } from "./setup-page";

type DirectionView = OperationOutput<"agentDirection.get">;
type Setting = DirectionView["setting"];
export type DirectionLevel = Setting["level"];

/** The three levels, in order, with their (i) text (house rule: short labels, tips behind). */
export const LEVELS: readonly { value: DirectionLevel; label: string; tip: string }[] = [
  {
    value: "hands_off",
    label: "Hands-off",
    tip: "Other agents direct your agents at once; you see it on the card.",
  },
  {
    value: "inform",
    label: "Tell me",
    tip: "Other agents act at once; you get an inbox item with Take back.",
  },
  {
    value: "ask",
    label: "Ask me first",
    tip: "For what you tick, a request waits for you until the timeout.",
  },
];
export const levelLabel = (l: DirectionLevel) => LEVELS.find((x) => x.value === l)?.label ?? l;

export const directionKey = (co: string) => ["company", co, "agent-direction"] as const;
export const directionQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: directionKey(co),
    queryFn: () => api.client.agentDirection.get({ params: { co } }),
  });

export function AgentDirectionScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const agents = useFeature("agents");
  const q = useQuery({ ...directionQuery(api, company.id), enabled: agents.enabled });
  return (
    <SetupPage title="Who may direct my agents" icon={WaypointsIcon} slot="setup-agent-direction">
      {!agents.enabled ? (
        <AgentsOffBanner company={company} className="" />
      ) : q.isPending ? (
        <Loading label="Loading who may direct your agents" rows={4} />
      ) : q.isError ? (
        <LoadError what="Who may direct my agents" error={q.error} />
      ) : (
        <DirectionForm api={api} co={company.id} view={q.data} />
      )}
    </SetupPage>
  );
}

type Draft = {
  level: DirectionLevel;
  assign: boolean;
  priority: boolean;
  timeout_hours: string;
  fallback: Setting["fallback"];
};

const draftOf = (s: Setting): Draft => ({
  level: s.level,
  assign: s.ask_for.includes("assign"),
  priority: s.ask_for.includes("priority"),
  timeout_hours: String(s.timeout_hours),
  fallback: s.fallback,
});

const hoursOf = (v: string) => {
  const n = Number(v);
  return v.trim() !== "" && Number.isInteger(n) && n >= 1 && n <= 24 ? n : null;
};

function DirectionForm({ api, co, view }: { api: Api; co: string; view: DirectionView }) {
  const qc = useQueryClient();
  const id = useId();
  const [draft, setDraft] = useState<Draft>(() => draftOf(view.setting));
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: (body: Setting) => api.client.agentDirection.put({ params: { co }, body }),
    onSuccess: (v) => {
      setDraft(draftOf(v.setting));
      setSavedAt(Date.now());
      qc.setQueryData(directionKey(co), v);
    },
  });
  const set = (patch: Partial<Draft>) => {
    save.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };
  const locked = view.locked;
  const ask = draft.level === "ask";
  const hours = hoursOf(draft.timeout_hours);
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(view.setting));
  const body: Setting | null =
    hours === null
      ? null
      : {
          level: draft.level,
          ask_for: [
            ...(draft.assign ? (["assign"] as const) : []),
            ...(draft.priority ? (["priority"] as const) : []),
          ],
          timeout_hours: hours,
          fallback: draft.fallback,
        };
  return (
    <form
      aria-label="Who may direct my agents"
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (body && !locked) save.mutate(body);
      }}
    >
      <SettingsSection id="level" icon={WaypointsIcon} title="Level">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span data-slot="direction-agents">
            Your agents: <span className="font-medium text-foreground">{view.agents}</span>
          </span>
          <span aria-hidden="true">·</span>
          <span data-slot="direction-mode">
            Mode:{" "}
            <span className="font-medium text-foreground">
              {view.mode === "team" ? "Team" : "Solo"}
            </span>
          </span>
          <InfoTip label="About Mode">
            Your level applies when the Chief of Staff runs in Team mode. In Solo mode it directs
            every agent at once.
          </InfoTip>
          {locked ? (
            <Badge variant="secondary" data-slot="direction-locked">
              <LockIcon aria-hidden="true" />
              Set by the company
            </Badge>
          ) : null}
        </div>
        <fieldset disabled={locked} className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="sr-only">Level</legend>
          <div className="flex flex-wrap gap-2">
            {LEVELS.map((l) => (
              <div key={l.value} className="flex items-center gap-1">
                <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm has-checked:border-primary has-checked:bg-primary-soft has-disabled:cursor-not-allowed has-disabled:opacity-70">
                  <input
                    type="radio"
                    name={`${id}-level`}
                    value={l.value}
                    checked={draft.level === l.value}
                    disabled={locked}
                    onChange={() => set({ level: l.value })}
                    className="accent-primary"
                  />
                  {l.label}
                </label>
                <InfoTip label={`About ${l.label}`}>{l.tip}</InfoTip>
              </div>
            ))}
          </div>
        </fieldset>
        {locked ? (
          <p className="mt-3 text-xs text-muted-foreground">
            The company's level: {levelLabel(view.company_default)}.
          </p>
        ) : null}
      </SettingsSection>
      <SettingsSection id="ask-first" icon={ShieldCheckIcon} title="Ask me first">
        <fieldset disabled={locked || !ask} className="m-0 flex flex-col gap-4 border-0 p-0">
          <legend className="sr-only">Ask me first for</legend>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${id}-assign`}
                checked={draft.assign}
                disabled={locked || !ask}
                onCheckedChange={(on) => set({ assign: on === true })}
              />
              <Label htmlFor={`${id}-assign`} className="font-normal">
                New work
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${id}-priority`}
                checked={draft.priority}
                disabled={locked || !ask}
                onCheckedChange={(on) => set({ priority: on === true })}
              />
              <Label htmlFor={`${id}-priority`} className="font-normal">
                Priorities
              </Label>
              <InfoTip label="About Priorities">
                Priorities and deadlines in your department.
              </InfoTip>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <Field className="w-40">
              <div className="flex items-center gap-1">
                <FieldLabel htmlFor={`${id}-hours`}>Timeout (hours)</FieldLabel>
                <InfoTip label="About Timeout (hours)">
                  Working hours, 1 to 24; quiet hours do not count.
                </InfoTip>
              </div>
              <Input
                id={`${id}-hours`}
                type="number"
                inputMode="numeric"
                min={1}
                max={24}
                step={1}
                className="h-10"
                value={draft.timeout_hours}
                disabled={locked || !ask}
                aria-invalid={hours === null}
                onChange={(e) => set({ timeout_hours: e.target.value })}
              />
            </Field>
            <Field className="w-56">
              <FieldLabel htmlFor={`${id}-fallback`}>After the timeout</FieldLabel>
              <NativeSelect
                id={`${id}-fallback`}
                className="w-full"
                value={draft.fallback}
                disabled={locked || !ask}
                onChange={(e) => set({ fallback: e.target.value as Setting["fallback"] })}
              >
                <NativeSelectOption value="proceed">Proceed</NativeSelectOption>
                <NativeSelectOption value="escalate">Escalate to CEO</NativeSelectOption>
              </NativeSelect>
            </Field>
          </div>
        </fieldset>
      </SettingsSection>
      <SettingsSection id="fixed" icon={LockIcon} title="Always">
        <dl className="m-0 flex flex-col divide-y divide-border-subtle" data-slot="direction-fixed">
          <div className="flex items-center justify-between gap-4 py-2 first:pt-0">
            <dt className="flex items-center gap-1 text-sm">
              Urgent
              <InfoTip label="About Urgent">
                A deadline today or a blocked CEO priority; the agent says why on the card.
              </InfoTip>
            </dt>
            <dd className="m-0 text-sm text-muted-foreground">Always act and tell you</dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-2 last:pb-0">
            <dt className="text-sm">Instructions and configuration</dt>
            <dd className="m-0 text-sm text-muted-foreground">Never</dd>
          </div>
        </dl>
      </SettingsSection>
      {save.error ? (
        <Notice tone="danger" role="alert">
          {problemText(save.error, "The setting could not be saved.")}
        </Notice>
      ) : null}
      {locked ? null : (
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!dirty || !body || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          <SaveIndicator savedAt={savedAt} />
        </div>
      )}
    </form>
  );
}
